import React, { useEffect, useMemo, useState } from 'react';
import styles from './ReviewVideo.module.scss';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../components/Button/Button';
import Icon from '../../../../components/Icon/Icon';
import CommentsTab from '../../../../components/CommentsTab/CommentsTab';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import ConfirmApprovalsCommentModal from '../../../../components/ConfirmApprovalsCommentModal/ConfirmApprovalsCommentModal';
import ApproverInfo from '../../../../components/ApproverInfo/ApproverInfo';
import Spinner from '../../../../../common/components/Spinner';
import { useHistory } from 'react-router-dom';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import { parseString } from '../../../../utils/stringHelper';

const commentInit = {
  authorName: '',
  content: '',
  parent: null,
  type: 2,
  username: '',
};

const approvalTypes = {
  approve: 'approve',
  decline: 'decline',
  revoke: 'revoke',
};

const commentsType = 2;

const ReviewVideo = ({
  isMobileView,
  isPending,
  isApproved,
  storyId,
  reviewer,
  handleOpenTools,
  handleCloseReviewMode,
  approveVideo,
  declineVideo,
  revokeVideo,
  miniSidebar,
}) => {
  const [approvalsComments, setApprovalsComments] = useState([]);
  const [isApprovalsCommentsLoading, setIsApprovalsCommentsLoading] = useState(false);

  const [newComment, setNewComment] = useState(commentInit);
  const [isShowCommentConfirmationModal, setIsShowCommentConfirmationModal] = useState(false);
  const [approvalType, setApprovalType] = useState(null);

  const [loadingStates, setLoadingStates] = useState({ approve: false, decline: false, revoke: false });

  const isLoading = useMemo(() => Object.values(loadingStates).some(item => item), [loadingStates]);

  const { t } = useTranslation();

  const history = useHistory();

  useEffect(() => {
    getApprovalsComments();
  }, []);

  useEffect(() => {
    if (miniSidebar) {
      handleCloseReviewMode();
    }
  }, [miniSidebar]);

  const getApprovalsComments = async () => {
    try {
      setIsApprovalsCommentsLoading(true);
      const { result } = await CmsAPI.getComments(storyId, commentsType);
      setApprovalsComments(result || []);
    } catch (err) {
      console.log(err);
    } finally {
      setIsApprovalsCommentsLoading(false);
    }
  };

  const handleApproveVideo = async () => {
    if (newComment.content) {
      setApprovalType(approvalTypes.approve);
      setIsShowCommentConfirmationModal(true);
      return;
    }

    try {
      setLoadingStates({ ...loadingStates, approve: true });
      await approveVideo();

      if (isMobileView) {
        handleCloseReviewMode();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStates({ ...loadingStates, approve: false });
    }
  };

  const handleDeclineVideo = async () => {
    if (newComment.content) {
      setApprovalType(approvalTypes.decline);
      setIsShowCommentConfirmationModal(true);
      return;
    }

    try {
      setLoadingStates({ ...loadingStates, decline: true });
      await declineVideo();

      if (isMobileView) {
        handleCloseReviewMode();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStates({ ...loadingStates, decline: false });
    }
  };

  const handleRevokeVideo = async () => {
    if (newComment.content) {
      setApprovalType(approvalTypes.revoke);
      setIsShowCommentConfirmationModal(true);
      return;
    }

    try {
      setLoadingStates({ ...loadingStates, revoke: true });
      await revokeVideo();
      history.push(`/eg/review`);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStates({ ...loadingStates, revoke: false });
    }
  };

  return (
    <div className={cx(styles.wrapper, { [styles.mobileWrapper]: isMobileView })}>
      <div className={styles.descriptionWrapper}>
        <div className={styles.reviewHeader}>
          <h2>{t('general.video_action_button.review')}</h2>
          {isMobileView ? <CloseButton onClose={handleCloseReviewMode} /> : null}
        </div>
        {reviewer ? <ApproverInfo img={reviewer?.avatar} fullName={parseString(reviewer?.name)} /> : null}
        <CommentsTab
          updateComments={getApprovalsComments}
          isCommentsLoading={isApprovalsCommentsLoading}
          storyId={storyId}
          comments={approvalsComments}
          newComment={newComment}
          commentInit={commentInit}
          setNewComment={setNewComment}
        />
      </div>
      <div className={styles.btnWrapper}>
        <div className={styles.line} />
        {isMobileView ? null : (
          <CustomButton
            variant="primary"
            label={t('approvals.approver.make_changes_to_video')}
            onClick={handleOpenTools}
            disabled={isLoading}
          />
        )}
        {isPending ? (
          <div>
            <CustomButton
              variant="primary"
              label={t('general.video_action_button.approve')}
              onClick={handleApproveVideo}
              disabled={isLoading}
              loading={loadingStates.approve}
              loadingSpinner={true}
            />
            <CustomButton
              variant="secondary"
              label={t('general.video_action_button.decline')}
              onClick={handleDeclineVideo}
              disabled={isLoading}
              loading={loadingStates.decline}
              loadingSpinner={true}
            />
          </div>
        ) : null}
        {isApproved ? (
          <CustomButton
            variant="primary"
            label={t('general.video_action_button.revoke')}
            onClick={handleRevokeVideo}
            disabled={isLoading}
            loading={loadingStates.revoke}
            loadingSpinner={true}
          />
        ) : null}
      </div>
      {isShowCommentConfirmationModal ? (
        <ConfirmApprovalsCommentModal
          isShow={isShowCommentConfirmationModal}
          onClose={() => setIsShowCommentConfirmationModal(false)}
          approvalType={approvalType}
          approvalTypes={approvalTypes}
          approveVideo={approveVideo}
          declineVideo={declineVideo}
          revokeVideo={revokeVideo}
        />
      ) : null}
    </div>
  );
};

export default ReviewVideo;
